import React, { useState } from "react";
import { useLang } from "../i18n";
import { Reveal, SectionLabel } from "./Motion";
import { Check, Loader2, Send } from "lucide-react";

export const LeadForm = ({ source, interests, label }) => {
  const { t } = useLang();
  const f = t.form;
  const [data, setData] = useState({
    name: "",
    email: "",
    company: "",
    interest: interests?.[0] || "",
    message: "",
    website: "",
    privacy: false,
  });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) =>
    setData((d) => ({ ...d, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 15000);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, source }),
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      if (!res.ok) throw new Error("request failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[var(--border)] rounded-xl px-4 py-3.5 font-outfit font-light text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--gold)] transition-colors";

  return (
    <section id="richiedi-info" data-testid={`lead-section-${source}`} className="relative py-20 md:py-28 bg-[var(--bg-secondary)] border-t border-[var(--border)]">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <Reveal>
          {label && <SectionLabel>{label}</SectionLabel>}
          <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)]">
            {f.title}
          </h2>
          <p className="font-outfit font-light text-[var(--text-secondary)] mt-4 max-w-2xl">{f.sub}</p>
        </Reveal>

        {status === "success" ? (
          <Reveal>
            <div data-testid="lead-success" className="mt-12 rounded-2xl border border-[var(--green)]/40 bg-[var(--green)]/10 p-8 flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-[var(--green)] flex items-center justify-center shrink-0">
                <Check className="w-5 h-5 text-white" />
              </span>
              <div>
                <p className="font-cormorant text-2xl text-[var(--text-primary)]">{f.successTitle}</p>
                <p className="font-outfit font-light text-[var(--text-secondary)] mt-1">{f.successText}</p>
              </div>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="lead-form" className="mt-12 grid sm:grid-cols-2 gap-5">
              {/* honeypot antispam: invisible to humans */}
              <input
                type="text"
                value={data.website}
                onChange={set("website")}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute -left-[9999px] opacity-0 h-0 w-0"
              />
              <input required value={data.name} onChange={set("name")} placeholder={f.name} data-testid="lead-name" className={inputCls} />
              <input required type="email" value={data.email} onChange={set("email")} placeholder={f.email} data-testid="lead-email" className={inputCls} />
              <input value={data.company} onChange={set("company")} placeholder={f.company} data-testid="lead-company" className={inputCls} />
              {interests?.length > 0 && (
                <select value={data.interest} onChange={set("interest")} data-testid="lead-interest" aria-label={f.interest} className={`${inputCls} appearance-none cursor-pointer`}>
                  {interests.map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              )}
              <textarea required rows={5} value={data.message} onChange={set("message")} placeholder={f.message} data-testid="lead-message" className={`${inputCls} sm:col-span-2 resize-y`} />
              <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer group">
                <input required type="checkbox" checked={data.privacy} onChange={set("privacy")} data-testid="lead-privacy" className="mt-1 w-4 h-4 accent-[var(--green)] cursor-pointer" />
                <span className="font-outfit font-light text-sm text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors">{f.privacy}</span>
              </label>
              {status === "error" && (
                <p data-testid="lead-error" className="sm:col-span-2 font-outfit text-sm text-red-600">{f.error}</p>
              )}
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="lead-submit"
                className="sm:col-span-2 inline-flex items-center justify-center gap-3 rounded-full bg-[var(--gold)] text-white px-8 py-4 font-outfit text-xs tracking-[0.2em] uppercase hover:opacity-90 disabled:opacity-60 transition-opacity"
              >
                {status === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {status === "sending" ? f.sending : f.submit}
              </button>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};
